import type { ReactElement } from 'react'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { IconButton } from '@/components/ui'
import { formatNumber } from '@/lib/format'

export interface PaginationProps {
  page: number
  totalPages: number
  onPageChange: (page: number) => void
  disabled?: boolean
}

/** Past this many pages, stepping one at a time stops being enough. */
const JUMP_THRESHOLD = 4

export function Pagination({
  page,
  totalPages,
  onPageChange,
  disabled = false
}: PaginationProps): ReactElement | null {
  // One page needs no controls at all, and an empty result set needs none either.
  if (totalPages <= 1) return null

  const first = page <= 1
  const last = page >= totalPages
  const showJumps = totalPages > JUMP_THRESHOLD

  return (
    <nav aria-label="Pagination" className="flex items-center justify-center gap-1.5 py-1">
      {showJumps ? (
        <IconButton
          label="First page"
          variant="secondary"
          size="sm"
          disabled={disabled || first}
          onClick={() => onPageChange(1)}
        >
          <ChevronsLeft aria-hidden="true" />
        </IconButton>
      ) : null}

      <IconButton
        label="Previous page"
        variant="secondary"
        size="sm"
        disabled={disabled || first}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft aria-hidden="true" />
      </IconButton>

      <p aria-live="polite" className="px-3 text-xs font-medium tabular-nums text-fg-muted">
        Page {formatNumber(page)} of {formatNumber(totalPages)}
      </p>

      <IconButton
        label="Next page"
        variant="secondary"
        size="sm"
        disabled={disabled || last}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight aria-hidden="true" />
      </IconButton>

      {showJumps ? (
        <IconButton
          label="Last page"
          variant="secondary"
          size="sm"
          disabled={disabled || last}
          onClick={() => onPageChange(totalPages)}
        >
          <ChevronsRight aria-hidden="true" />
        </IconButton>
      ) : null}
    </nav>
  )
}
